import { useState, useMemo, useEffect } from "react";
import { CheckCircle2, ArrowRight, ArrowLeft, Sparkles, Lightbulb } from "lucide-react";
import LessonVideo from "@/components/LessonVideo";

/**
 * MiddleSchoolLesson1Flow
 * Orchestrates a focused, step-by-step experience for Middle School Lesson 1:
 *  Stage A: Spark Your Idea (4 multi-choice questions, "Question X of 4")
 *  Stage B: Entrepreneur Profile reveal (DISC-style)
 *  Stage C: Step-by-step Activity (3 sequential text questions)
 *  Stage D: Build Your Business Idea (problem / solution / business name)
 *
 * Props:
 *  - savedResponses: object — previously persisted nested state
 *  - onChange(nestedState): debounced auto-save handler
 *  - onContextChange(prompts): callback so parent can swap LemonBot suggested prompts
 *  - onReadyToSubmit(builderObj): called when builder is filled and student clicks "Finish Lesson 1"
 *  - submitting: bool
 */

const SPARK_QUESTIONS = [
  {
    id: "q1_problem",
    label: "Spot the Problem",
    purpose: "Which problem do you notice the most?",
    options: [
      { value: "bored", label: "People get bored during lunch or after school" },
      { value: "supplies", label: "Students forget or lose their supplies" },
      { value: "hot", label: "It's too hot and people need something refreshing" },
      { value: "style", label: "People want to look cool or express themselves" },
      { value: "other", label: "Other (write your own)" },
    ],
    allowOther: true,
  },
  {
    id: "q2_style",
    label: "Your Entrepreneur Style",
    purpose: "What kind of business excites you?",
    options: [
      { value: "products", label: "Selling products", disc: "D" },
      { value: "services", label: "Helping people (services)", disc: "S" },
      { value: "creative", label: "Creating something (art, videos, designs)", disc: "I" },
      { value: "tech", label: "Technology-based", disc: "C" },
    ],
  },
  {
    id: "q3_money",
    label: "How Do You Want to Make Money?",
    purpose: "Pick how you would rather earn money:",
    options: [
      { value: "fast", label: "Selling something quickly (fast sales, low price)", disc: "D" },
      { value: "premium", label: "Charging more for something special (premium)", disc: "I" },
      { value: "repeat", label: "Getting repeat customers (subscriptions or ongoing service)", disc: "S" },
      { value: "team", label: "Working with a team to build something bigger", disc: "C" },
    ],
  },
  {
    id: "q4_creative",
    label: "Creative Spark",
    purpose: "If you could create any business today, what would it be?",
    options: [
      { value: "food", label: "Food or drink business", disc: "D" },
      { value: "fashion", label: "Fashion or style brand", disc: "I" },
      { value: "service", label: "A service that helps people", disc: "S" },
      { value: "digital", label: "Digital or online business", disc: "C" },
      { value: "unique", label: "Something totally unique", disc: "I" },
    ],
  },
];

const ACTIVITY_STEPS = [
  {
    id: "problem_noticed",
    prompt: "What problem have you noticed in your school or neighborhood?",
    placeholder: "Describe a problem you've noticed people deal with...",
  },
  {
    id: "why_important",
    prompt: "Why is this problem important?",
    placeholder: "Who does it affect, and why does fixing it matter?",
  },
  {
    id: "three_ideas",
    prompt: "List three business ideas that could address this problem.",
    placeholder: "Idea 1: ...\nIdea 2: ...\nIdea 3: ...",
    multiline: true,
  },
];

const PROFILES = {
  D: {
    icon: "🦅",
    name: "The Builder",
    color: "from-red-400 to-orange-500",
    description:
      "You're bold, action-driven, and love getting things done. Builders make things happen, take charge, and are great at turning ideas into real products.",
    superpower: "Decisiveness + Drive",
  },
  I: {
    icon: "🦜",
    name: "The Promoter",
    color: "from-yellow-400 to-pink-500",
    description:
      "You're creative, expressive, and love connecting with people. Promoters are amazing at branding, storytelling, and making businesses fun and exciting.",
    superpower: "Creativity + Communication",
  },
  S: {
    icon: "🕊",
    name: "The Helper",
    color: "from-green-400 to-emerald-500",
    description:
      "You care about people and love making their lives easier. Helpers build trusted services and businesses that customers come back to again and again.",
    superpower: "Empathy + Reliability",
  },
  C: {
    icon: "🦚",
    name: "The Strategist",
    color: "from-blue-400 to-indigo-500",
    description:
      "You're a thinker who loves planning, learning, and solving puzzles. Strategists turn smart ideas into structured plans and digital businesses that scale.",
    superpower: "Analysis + Vision",
  },
};

// Q1 -> contextual LemonBot prompts
const LEMONBOT_BY_PROBLEM = {
  bored: [
    "Help me think of a fun lunchtime business",
    "What entertainment or games could kids run at school?",
    "How can I make break time more exciting for classmates?",
  ],
  supplies: [
    "How can I help students stay organized?",
    "What product could solve forgotten supplies?",
    "Could a supply lending or rental business work?",
  ],
  hot: [
    "Give me 3 refreshing product ideas for hot days",
    "How can I make a lemonade stand stand out?",
    "What snacks or drinks would sell well in the heat?",
  ],
  style: [
    "What fashion or style business could I start?",
    "How can I design something kids want to wear?",
    "What trending products are popular with my age group?",
  ],
  other: [
    "Help me turn my problem into a business idea",
    "How do I know if my idea is good?",
    "Who would buy my solution?",
  ],
};

// Q1 / Q4 -> career pathway keys (must mirror backend standards.py)
const Q1_PATHWAYS = {
  bored: ["recreation_arts", "hospitality_food", "marketing_creative"],
  supplies: ["retail_logistics", "business_entrepreneurship"],
  hot: ["hospitality_food", "business_entrepreneurship"],
  style: ["marketing_creative", "retail_logistics"],
  other: ["business_entrepreneurship"],
};
const Q4_PATHWAYS = {
  food: ["hospitality_food"],
  fashion: ["marketing_creative", "retail_logistics"],
  service: ["human_services"],
  digital: ["tech_digital"],
  unique: ["business_entrepreneurship"],
};
const CAREER_PATHWAYS_DATA = {
  hospitality_food: { name: "Hospitality, Tourism & Food Services", icon: "🍋", in_demand: true },
  retail_logistics: { name: "Retail, Logistics & Product Design", icon: "📦", in_demand: true },
  marketing_creative: { name: "Marketing, Branding & Visual Arts", icon: "🎨", in_demand: true },
  tech_digital: { name: "Information Technology & Software", icon: "💻", in_demand: true },
  human_services: { name: "Human Services & Education", icon: "🤝", in_demand: true },
  business_entrepreneurship: { name: "Business Management & Entrepreneurship", icon: "🚀", in_demand: true },
  recreation_arts: { name: "Recreation, Sports & Performing Arts", icon: "🎭", in_demand: false },
};
function pathwaysFromSpark(spark) {
  const keys = [];
  if (spark.q1_problem && Q1_PATHWAYS[spark.q1_problem]) keys.push(...Q1_PATHWAYS[spark.q1_problem]);
  if (spark.q4_creative && Q4_PATHWAYS[spark.q4_creative]) keys.push(...Q4_PATHWAYS[spark.q4_creative]);
  const seen = new Set();
  return keys
    .filter((k) => !seen.has(k) && CAREER_PATHWAYS_DATA[k] && (seen.add(k), true))
    .map((k) => ({ key: k, ...CAREER_PATHWAYS_DATA[k] }));
}

function calculateProfile(spark) {
  const tally = { D: 0, I: 0, S: 0, C: 0 };
  ["q2_style", "q3_money", "q4_creative"].forEach((qid) => {
    const q = SPARK_QUESTIONS.find((x) => x.id === qid);
    const ans = spark[qid];
    const opt = q?.options.find((o) => o.value === ans);
    if (opt?.disc) tally[opt.disc] += 1;
  });
  // pick highest, tie-breaker: I > S > D > C
  const order = ["I", "S", "D", "C"];
  let best = "I";
  let max = -1;
  order.forEach((k) => {
    if (tally[k] > max) {
      max = tally[k];
      best = k;
    }
  });
  return { type: best, ...PROFILES[best], tally };
}

function MiddleSchoolLesson1Flow({
  savedResponses,
  onChange,
  onContextChange,
  onReadyToSubmit,
  submitting,
  videoUrl,
}) {
  // Top-level stage: 'spark' | 'profile' | 'activity' | 'builder'
  const [stage, setStage] = useState(savedResponses?.stage || "spark");
  const [sparkIdx, setSparkIdx] = useState(savedResponses?.sparkIdx ?? 0);
  const [activityIdx, setActivityIdx] = useState(savedResponses?.activityIdx ?? 0);
  const [spark, setSpark] = useState(savedResponses?.spark || {});
  const [activity, setActivity] = useState(savedResponses?.activity || {});
  const [builder, setBuilder] = useState(savedResponses?.builder || {});

  const profile = useMemo(() => calculateProfile(spark), [spark]);

  // Total master step indicator
  const SPARK_TOTAL = SPARK_QUESTIONS.length;
  const ACTIVITY_TOTAL = ACTIVITY_STEPS.length;
  const TOTAL_STEPS = SPARK_TOTAL + 1 /* profile */ + ACTIVITY_TOTAL + 1 /* builder */;
  let currentStep = 1;
  if (stage === "spark") currentStep = sparkIdx + 1;
  else if (stage === "profile") currentStep = SPARK_TOTAL + 1;
  else if (stage === "activity") currentStep = SPARK_TOTAL + 1 + activityIdx + 1;
  else if (stage === "builder") currentStep = TOTAL_STEPS;

  // Persist on any change (debounced by parent)
  useEffect(() => {
    onChange?.({ stage, sparkIdx, activityIdx, spark, activity, builder });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, sparkIdx, activityIdx, spark, activity, builder]);

  // Update LemonBot context whenever Q1 changes or stage changes
  useEffect(() => {
    const q1 = spark.q1_problem;
    if (q1 && LEMONBOT_BY_PROBLEM[q1]) {
      onContextChange?.(LEMONBOT_BY_PROBLEM[q1]);
    } else {
      onContextChange?.([
        "Help me think of a business idea",
        "What problem can I solve?",
        "How do I know if my idea is good?",
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spark.q1_problem]);

  // ===== Stage: Spark Quiz =====
  if (stage === "spark") {
    const q = SPARK_QUESTIONS[sparkIdx];
    const value = spark[q.id] || "";
    const otherValue = spark[`${q.id}_other`] || "";
    const canAdvance =
      !!value && (q.allowOther && value === "other" ? otherValue.trim().length > 0 : true);
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8" data-testid="ms-l1-spark">
        {videoUrl && sparkIdx === 0 && (
          <div className="mb-5">
            <LessonVideo url={videoUrl} title="What is an Entrepreneur?" />
          </div>
        )}
        <MasterProgress current={currentStep} total={TOTAL_STEPS} />
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-5 h-5 text-purple-500" />
          <h2 className="text-xl font-bold text-gray-800">Spark Your Idea</h2>
          <span
            className="ml-auto text-xs font-bold text-purple-600 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-full"
            data-testid="ms-l1-spark-position"
          >
            Question {sparkIdx + 1} of {SPARK_TOTAL}
          </span>
        </div>
        <h3 className="text-lg font-bold text-gray-900 mt-3 mb-1">{q.label}</h3>
        <p className="text-gray-600 mb-5">{q.purpose}</p>

        <div className="space-y-3" role="radiogroup" aria-label={q.purpose}>
          {q.options.map((opt) => {
            const selected = value === opt.value;
            return (
              <label
                key={opt.value}
                className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  selected
                    ? "bg-purple-50 border-purple-500 shadow-sm"
                    : "bg-white border-gray-200 hover:border-purple-300"
                }`}
                data-testid={`ms-l1-spark-${q.id}-${opt.value}`}
              >
                <input
                  type="radio"
                  name={q.id}
                  value={opt.value}
                  checked={selected}
                  onChange={() => setSpark((prev) => ({ ...prev, [q.id]: opt.value }))}
                  className="mt-1 accent-purple-500"
                />
                <span className="text-gray-800 font-medium">{opt.label}</span>
              </label>
            );
          })}
        </div>

        {q.allowOther && value === "other" && (
          <input
            type="text"
            value={otherValue}
            onChange={(e) =>
              setSpark((prev) => ({ ...prev, [`${q.id}_other`]: e.target.value }))
            }
            placeholder="Tell us your own answer..."
            className="mt-4 w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:outline-none focus:border-purple-500"
            data-testid={`ms-l1-spark-${q.id}-other-input`}
          />
        )}

        <div className="flex justify-between items-center gap-3 mt-6 flex-wrap">
          {sparkIdx > 0 ? (
            <button
              onClick={() => setSparkIdx(sparkIdx - 1)}
              className="flex items-center gap-1 px-4 py-2 bg-white border-2 border-purple-300 text-purple-700 font-semibold rounded-lg hover:bg-purple-50"
              data-testid="ms-l1-spark-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          ) : <div />}
          <button
            onClick={() => {
              if (sparkIdx < SPARK_TOTAL - 1) setSparkIdx(sparkIdx + 1);
              else setStage("profile");
            }}
            disabled={!canAdvance}
            className="flex items-center gap-1 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-xl shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="ms-l1-spark-next-btn"
          >
            {sparkIdx < SPARK_TOTAL - 1 ? "Next" : "Reveal My Profile"}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // ===== Stage: Profile Reveal =====
  if (stage === "profile") {
    const careerPathways = pathwaysFromSpark(spark);
    return (
      <div
        className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 text-center"
        data-testid="ms-l1-profile"
      >
        <MasterProgress current={currentStep} total={TOTAL_STEPS} />
        <div
          className={`bg-gradient-to-br ${profile.color} rounded-2xl p-8 text-white mb-6`}
        >
          <div className="text-7xl mb-3" data-testid="ms-l1-profile-icon">{profile.icon}</div>
          <div className="text-sm uppercase tracking-widest opacity-90 mb-1">
            Your Entrepreneur Profile
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-3" data-testid="ms-l1-profile-name">
            {profile.name}
          </h2>
          <p className="text-base sm:text-lg opacity-95 max-w-xl mx-auto">
            {profile.description}
          </p>
          <div className="mt-4 inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold">
            ⚡ Superpower: {profile.superpower}
          </div>
        </div>

        {/* Career Pathway Suggestions */}
        {careerPathways.length > 0 && (
          <div className="mb-6 text-left" data-testid="ms-l1-career-pathways">
            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <span className="text-xl">💼</span> Suggested Career Pathways
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {careerPathways.map((p) => (
                <div
                  key={p.key}
                  className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-3 flex items-center gap-2"
                  data-testid={`ms-l1-pathway-${p.key}`}
                >
                  <span className="text-2xl">{p.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-gray-900 text-sm">{p.name}</div>
                    {p.in_demand && (
                      <span className="text-[10px] font-bold bg-green-200 text-green-800 px-2 py-0.5 rounded-full">
                        ⭐ IN-DEMAND
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-gray-600 mb-6">
          Awesome! Now let's turn your spark into a real business idea — step by step.
        </p>
        <div className="flex justify-center gap-3 flex-wrap">
          <button
            onClick={() => setStage("spark")}
            className="px-5 py-2 bg-white border-2 border-purple-300 text-purple-700 font-semibold rounded-lg hover:bg-purple-50"
            data-testid="ms-l1-profile-retake-btn"
          >
            Retake Quiz
          </button>
          <button
            onClick={() => setStage("activity")}
            className="flex items-center gap-1 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-xl shadow-md hover:shadow-lg"
            data-testid="ms-l1-profile-continue-btn"
          >
            Start the Activity <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // ===== Stage: Step-by-step Activity =====
  if (stage === "activity") {
    const step = ACTIVITY_STEPS[activityIdx];
    const value = activity[step.id] || "";
    const canAdvance = value.trim().length > 0;
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8" data-testid="ms-l1-activity">
        <MasterProgress current={currentStep} total={TOTAL_STEPS} />
        <div className="flex items-center gap-2 mb-2">
          <span className="text-2xl">🎯</span>
          <h2 className="text-xl font-bold text-gray-800">Activity</h2>
          <span
            className="ml-auto text-xs font-bold text-pink-600 bg-pink-50 border border-pink-200 px-2 py-0.5 rounded-full"
            data-testid="ms-l1-activity-position"
          >
            Step {activityIdx + 1} of {ACTIVITY_TOTAL}
          </span>
        </div>

        <h3 className="text-lg font-bold text-gray-900 mt-3 mb-3">{step.prompt}</h3>

        {step.multiline ? (
          <textarea
            value={value}
            onChange={(e) => setActivity((prev) => ({ ...prev, [step.id]: e.target.value }))}
            placeholder={step.placeholder}
            rows={5}
            className="w-full px-4 py-3 border-2 border-pink-200 rounded-lg focus:outline-none focus:border-pink-500"
            data-testid={`ms-l1-activity-${step.id}`}
          />
        ) : (
          <input
            type="text"
            value={value}
            onChange={(e) => setActivity((prev) => ({ ...prev, [step.id]: e.target.value }))}
            placeholder={step.placeholder}
            className="w-full px-4 py-3 border-2 border-pink-200 rounded-lg focus:outline-none focus:border-pink-500"
            data-testid={`ms-l1-activity-${step.id}`}
          />
        )}

        <div className="flex justify-between items-center gap-3 mt-6 flex-wrap">
          <button
            onClick={() => {
              if (activityIdx > 0) setActivityIdx(activityIdx - 1);
              else setStage("profile");
            }}
            className="flex items-center gap-1 px-4 py-2 bg-white border-2 border-pink-300 text-pink-700 font-semibold rounded-lg hover:bg-pink-50"
            data-testid="ms-l1-activity-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button
            onClick={() => {
              if (activityIdx < ACTIVITY_TOTAL - 1) setActivityIdx(activityIdx + 1);
              else setStage("builder");
            }}
            disabled={!canAdvance}
            className="flex items-center gap-1 px-6 py-3 bg-gradient-to-r from-pink-500 to-orange-400 text-white font-bold rounded-xl shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="ms-l1-activity-next-btn"
          >
            {activityIdx < ACTIVITY_TOTAL - 1 ? "Next" : "Finish Activity"}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // ===== Stage: Build Your Business Idea =====
  // builder stage
  const builderReady =
    (builder.problem || "").trim() &&
    (builder.solution || "").trim() &&
    (builder.business_name || "").trim();
  return (
    <div
      className="bg-gradient-to-br from-yellow-50 via-white to-orange-50 rounded-2xl shadow-lg p-6 sm:p-8 border-2 border-yellow-200"
      data-testid="ms-l1-builder"
    >
      <MasterProgress current={currentStep} total={TOTAL_STEPS} />
      <div className="flex items-center gap-2 mb-2">
        <Lightbulb className="w-6 h-6 text-orange-500" />
        <h2 className="text-xl font-bold text-gray-800">Build Your Business Idea</h2>
      </div>
      <p className="text-gray-600 mb-5">
        Use what you wrote in the activity to craft your very first business idea.
      </p>

      <div className="space-y-4">
        <div>
          <label htmlFor="bld-problem" className="block font-semibold text-gray-800 mb-1">
            Problem
          </label>
          <input
            id="bld-problem"
            type="text"
            value={builder.problem || ""}
            onChange={(e) => setBuilder((prev) => ({ ...prev, problem: e.target.value }))}
            placeholder="The problem I want to solve..."
            className="w-full px-4 py-3 border-2 border-orange-200 rounded-lg focus:outline-none focus:border-orange-500"
            data-testid="ms-l1-builder-problem"
          />
        </div>
        <div>
          <label htmlFor="bld-solution" className="block font-semibold text-gray-800 mb-1">
            Solution
          </label>
          <textarea
            id="bld-solution"
            value={builder.solution || ""}
            onChange={(e) => setBuilder((prev) => ({ ...prev, solution: e.target.value }))}
            placeholder="Here's how I would solve it..."
            rows={3}
            className="w-full px-4 py-3 border-2 border-orange-200 rounded-lg focus:outline-none focus:border-orange-500"
            data-testid="ms-l1-builder-solution"
          />
        </div>
        <div>
          <label htmlFor="bld-name" className="block font-semibold text-gray-800 mb-1">
            Business Name
          </label>
          <input
            id="bld-name"
            type="text"
            value={builder.business_name || ""}
            onChange={(e) =>
              setBuilder((prev) => ({ ...prev, business_name: e.target.value }))
            }
            placeholder="What would you call your business?"
            className="w-full px-4 py-3 border-2 border-orange-200 rounded-lg focus:outline-none focus:border-orange-500"
            data-testid="ms-l1-builder-name"
          />
        </div>
      </div>

      <div className="flex justify-between items-center gap-3 mt-6 flex-wrap">
        <button
          onClick={() => setStage("activity")}
          className="flex items-center gap-1 px-4 py-2 bg-white border-2 border-orange-300 text-orange-700 font-semibold rounded-lg hover:bg-orange-50"
          data-testid="ms-l1-builder-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Activity
        </button>
        <button
          onClick={() => onReadyToSubmit?.({ ...builder, profile: profile.type })}
          disabled={!builderReady || submitting}
          className="flex items-center gap-1 px-8 py-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
          data-testid="ms-l1-builder-submit-btn"
        >
          {submitting ? "Saving..." : "Finish Lesson 1 🎉"}
          {!submitting && <CheckCircle2 className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
}

function MasterProgress({ current, total }) {
  const pct = Math.round((current / total) * 100);
  return (
    <div className="mb-5" data-testid="ms-l1-master-progress">
      <div className="flex justify-between text-xs font-semibold text-gray-500 mb-1">
        <span>Step {current} of {total}</span>
        <span>{pct}%</span>
      </div>
      <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default MiddleSchoolLesson1Flow;
